import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UrlParameter } from '@shared/services/url-parameters-getter/url-parameters-getter.config';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UrlParametersGetterService {
  private urlChangeSubject = new BehaviorSubject<string>(this.router.url ?? '');
  private urlChangeSubject$ = this.urlChangeSubject.asObservable();

  private subscription = new Subscription();

  /**
   * We don't need to unsubscribe because the service is singleton, and we cannot get memory leaks
   * @param router
   */
  constructor(private router: Router) {
    this.subscription.add(
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          distinctUntilChanged()
        )
        .subscribe((event: NavigationEnd) =>
          this.urlChangeSubject.next(event.url)
        )
    );
  }

  public getUrlParameters(parameter: UrlParameter): number {
    return this.urlParameters.get(parameter);
  }

  public get urlParameters(): Map<string, number> {
    return UrlParametersGetterService.extractUrlParameters(
      this.urlChangeSubject.value
    );
  }

  public get urlParameters$(): Observable<Map<string, number>> {
    return this.urlChangeSubject$.pipe(
      map(url => UrlParametersGetterService.extractUrlParameters(url))
    );
  }

  public getParameter$(
    parameter: UrlParameter
  ): Observable<number | undefined> {
    return this.urlParameters$.pipe(
      map(parameters => parameters.get(parameter))
    );
  }

  private static extractUrlParameters(url: string): Map<string, number> {
    const urlSplitted = url.split('/');
    const parameters = new Map<string, number>();
    for (let i = 0; i < urlSplitted.length; i++) {
      if (isNaN(Number(urlSplitted[i])) && !isNaN(Number(urlSplitted[i + 1]))) {
        parameters.set(urlSplitted[i], Number(urlSplitted[i + 1]));
      } else if (!urlSplitted[i + 1] && isNaN(Number(urlSplitted[i]))) {
        parameters.set(urlSplitted[i], 1);
      }
    }
    return parameters;
  }
}
